import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getModuleHelpConfig, getModuleHelpSection } from "../config/moduleHelp";

const PANEL_KEY = "agdysa.cms.helpPanel.enabled";
const seenKey = (moduleId) => `agdysa.cms.helpSeen.${moduleId}`;

function readPanelEnabled() {
  try {
    return localStorage.getItem(PANEL_KEY) === "true";
  } catch {
    return false;
  }
}

function writePanelEnabled(value) {
  try {
    localStorage.setItem(PANEL_KEY, value ? "true" : "false");
  } catch {
    /* ignore */
  }
}

function readIntroSeen(moduleId) {
  try {
    return localStorage.getItem(seenKey(moduleId)) === "1";
  } catch {
    return false;
  }
}

function writeIntroSeen(moduleId) {
  try {
    localStorage.setItem(seenKey(moduleId), "1");
  } catch {
    /* ignore */
  }
}

const ModuleHelpContext = createContext(null);

export function ModuleHelpProvider({ moduleId, children }) {
  const config = getModuleHelpConfig(moduleId);
  const [panelEnabled, setPanelEnabledState] = useState(readPanelEnabled);
  const [sectionId, setSectionId] = useState(config?.defaultSectionId ?? "modulo-intro");
  const [introOpen, setIntroOpen] = useState(false);

  useEffect(() => {
    if (!config) return;
    if (!readIntroSeen(moduleId)) {
      setIntroOpen(true);
    }
  }, [config, moduleId]);

  const setPanelEnabled = useCallback((value) => {
    setPanelEnabledState(value);
    writePanelEnabled(value);
  }, []);

  const togglePanel = useCallback(() => {
    setPanelEnabled(!panelEnabled);
  }, [panelEnabled, setPanelEnabled]);

  const dismissIntro = useCallback(
    (options) => {
      writeIntroSeen(moduleId);
      setIntroOpen(false);
      if (options?.enablePanel) {
        setPanelEnabled(true);
      }
    },
    [moduleId, setPanelEnabled]
  );

  const value = useMemo(() => {
    if (!config) return null;
    return {
      moduleId,
      config,
      panelEnabled,
      setPanelEnabled,
      togglePanel,
      sectionId,
      setSectionId,
      activeSection: getModuleHelpSection(config, sectionId),
      introOpen,
      dismissIntro,
    };
  }, [config, moduleId, panelEnabled, setPanelEnabled, togglePanel, sectionId, introOpen, dismissIntro]);

  if (!value) {
    return children;
  }

  return <ModuleHelpContext.Provider value={value}>{children}</ModuleHelpContext.Provider>;
}

export function useModuleHelp() {
  const ctx = useContext(ModuleHelpContext);
  if (!ctx) {
    throw new Error("useModuleHelp debe usarse dentro de ModuleHelpProvider");
  }
  return ctx;
}

export function useModuleHelpOptional() {
  return useContext(ModuleHelpContext);
}
